import { PropsWithChildren, useCallback, useContext } from 'react';
import { useFormik } from 'formik';
import { UserContext } from 'shared/Providers/User';
import { ROUTE } from 'shared/constants/routes_old';
import { FormikConfig } from 'formik/dist/types';
import { Box, Button, Checkbox, FormControlLabel, Link, Typography } from '@mui/material';
import { FormTextField } from 'shared/_ui/form/form-text-field';
import {
	FormBlock,
	FormBox,
	LoginDivider,
	SocialButtonsContainer,
} from 'shared/Pages/common/Login/components/styles';
import GoogleSsoButton from 'shared/Pages/common/Login/components/sso-providers/google-sso-button';
import { SsoButtonProps } from 'shared/Pages/common/Login/components/sso-providers/types';
import { webRegister } from 'shared/modules/user/actions';
import { useHistory } from 'react-router';
import PasswordField from 'shared/Pages/common/Login/components/password-field';
import {
	LOGIN_VALIDATION_SCHEMA,
	REGISTER_VALIDATION_SCHEMA,
} from 'shared/Pages/common/Login/constants';
import { isWeb } from 'shared/constants/modules';

function SsoButtons({ disabled, onSubmitUrl, params }: SsoButtonProps) {
	return (
		<SocialButtonsContainer>
			<GoogleSsoButton disabled={disabled} onSubmitUrl={onSubmitUrl} params={params} />
		</SocialButtonsContainer>
	);
}

type ContentProps = PropsWithChildren<{
	header: string;
	isSubmitting: boolean;
	or: string;
	rememberMe?: boolean;
	subHeader: string;
}>;

function Content(props: ContentProps) {
	const { children, header, isSubmitting, or, subHeader, rememberMe } = props;
	const handleSsoSubmit = (authUrl: string) => {
		window.open(authUrl, '_self');
	};

	return (
		<>
			<Box>
				<Typography
					variant="h1"
					sx={{
						textAlign: 'center',
					}}
				>
					{header}
				</Typography>
				<Typography
					sx={{
						mt: 2,
						textAlign: 'center',
					}}
				>
					{subHeader}
				</Typography>
			</Box>

			{isWeb && (
				<>
					<SsoButtons
						disabled={isSubmitting}
						onSubmitUrl={handleSsoSubmit}
						params={{ rememberMe }}
					/>
					<LoginDivider>{or}</LoginDivider>
				</>
			)}

			{children}
		</>
	);
}

type LoginFormValues = { email: string; password: string; rememberMe: boolean };

export default function Login() {
	const { login: handleLogin } = useContext(UserContext);

	const handleSubmit = useCallback<FormikConfig<LoginFormValues>['onSubmit']>(
		async (values, { setSubmitting }) => {
			setSubmitting(true);
			await handleLogin(values);
			setSubmitting(false);
		},
		[handleLogin]
	);

	const formik = useFormik<LoginFormValues>({
		initialValues: { email: '', password: '', rememberMe: false },
		validationSchema: LOGIN_VALIDATION_SCHEMA,
		onSubmit: handleSubmit,
	});

	return (
		<Content
			header="Welcome back"
			subHeader="Sign in to your account"
			or="or"
			isSubmitting={formik.isSubmitting}
			rememberMe={formik.values.rememberMe}
		>
			{/* <div className="error-message" id="errorMessage"> */}
			{/*	Invalid email or password. Please try again. */}
			{/* </div> */}

			<FormBox id="loginForm" onSubmit={formik.handleSubmit}>
				<FormTextField
					name="email"
					type="email"
					formik={formik}
					fullWidth
					label="Email address"
					labelProps={{ position: 'top' }}
					required
					placeholder="Enter your email address"
					disabled={formik.isSubmitting}
				/>
				<PasswordField
					disabled={formik.isSubmitting}
					formik={formik}
					label="Password"
					name="password"
					placeholder="Enter your password"
				/>

				<FormBlock sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
					<FormControlLabel
						control={
							<Checkbox
								disabled={formik.isSubmitting}
								checked={formik.values.rememberMe}
								name="rememberMe"
								onClick={() => formik.setFieldValue('rememberMe', !formik.values.rememberMe)}
							/>
						}
						label="Remember me"
					/>
					<Link href={ROUTE.resetPassword}>Forgot password?</Link>
				</FormBlock>

				<Button
					type="submit"
					size="large"
					id="submitBtn"
					fullWidth
					disabled={!formik.dirty || formik.isSubmitting || !formik.isValid}
				>
					Sign in
				</Button>

				<FormBlock sx={{ textAlign: 'center' }}>
					Don&apos;t have an account? <Link href={ROUTE.register}>Sign up</Link>
				</FormBlock>
			</FormBox>
		</Content>
	);
}

type RegisterFormValues = {
	email: string;
	name: string;
	password: string;
	passwordConfirm: string;
};

export function Register() {
	const history = useHistory();

	const handleSubmit = useCallback<FormikConfig<RegisterFormValues>['onSubmit']>(
		async (values, { setFieldError, setSubmitting }) => {
			setSubmitting(true);
			try {
				await webRegister(values);
				history.push(ROUTE.registerSuccess);
			} catch (e) {
				setFieldError('email', (e as Error)?.message);
			} finally {
				setSubmitting(false);
			}
		},
		[history]
	);

	const formik = useFormik<RegisterFormValues>({
		initialValues: { email: '', name: '', password: '', passwordConfirm: '' },
		validationSchema: REGISTER_VALIDATION_SCHEMA,
		onSubmit: handleSubmit,
	});

	return (
		<Content
			header="Create your account"
			subHeader="Join thousands of users already using Tiv"
			or="or sign up with email"
			isSubmitting={formik.isSubmitting}
		>
			<FormBox id="registerForm" onSubmit={formik.handleSubmit}>
				<FormTextField
					name="name"
					formik={formik}
					fullWidth
					label="Full name"
					labelProps={{ position: 'top' }}
					required
					placeholder="Enter your full name"
					disabled={formik.isSubmitting}
				/>
				<FormTextField
					name="email"
					type="email"
					formik={formik}
					fullWidth
					label="Email address"
					labelProps={{ position: 'top' }}
					required
					placeholder="Enter your email address"
					disabled={formik.isSubmitting}
				/>
				<PasswordField
					disabled={formik.isSubmitting}
					formik={formik}
					label="Password"
					name="password"
					placeholder="Create a strong password"
				/>
				<PasswordField
					disabled={formik.isSubmitting}
					formik={formik}
					label="Confirm password"
					name="passwordConfirm"
					placeholder="Confirm new password"
				/>

				<FormBlock sx={{ textAlign: 'end' }}>
					<Link href={ROUTE.resetPassword}>Forgot password?</Link>
				</FormBlock>

				<Button
					type="submit"
					size="large"
					fullWidth
					disabled={!formik.dirty || formik.isSubmitting || !formik.isValid}
				>
					Create account
				</Button>

				<FormBlock sx={{ textAlign: 'center' }}>
					Already have an account? <Link href={ROUTE.login}>Sign in</Link>
				</FormBlock>

				{/* <FormBlock sx={{ */}
				{/*	display: 'block', */}
				{/*	textAlign: 'center', */}
				{/*	color: (theme) => theme.palette.secondary.main, */}
				{/* }}> */}
				{/*	By creating an account, you agree to our <Link href={ROUTE.login}>Terms of Service</Link> and <Link href={ROUTE.login}>Privacy Policy</Link> */}
				{/* </FormBlock> */}
			</FormBox>
		</Content>
	);
}
